import { Link } from 'react-router-dom';
import { useCollection } from '@/hooks/useCollection';
import type { NewsArticle } from '@/lib/types';

function formatDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function NewsPreview() {
  const { rows: articles, loading, error, reload } = useCollection<NewsArticle>('news', 'published_on', false);

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">
              Latest Updates
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
              News &amp; Stories
            </h2>
            <div className="w-12 h-1 bg-accent-500 mt-5" />
          </div>
          <Link to="/news" className="btn-outline shrink-0">
            All News
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>

        {loading ? (
          <p className="text-center text-foreground-400 py-10">
            <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading news...
          </p>
        ) : error ? (
          <div className="text-center py-10">
            <p className="text-red-600 text-sm mb-3">Could not load news.</p>
            <button type="button" onClick={reload} className="btn-outline">Retry</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {articles.slice(0, 3).map((article) => (
              <article key={article.id} className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 hover:-translate-y-1 transition-all">
                <Link to="/news" aria-label={`Read: ${article.title}`}>
                  <div className="h-52 overflow-hidden">
                    <img
                      src={article.cover_image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Link>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="text-xs font-label font-semibold bg-accent-100 text-accent-800 px-2.5 py-1 rounded-full">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-400">{formatDate(article.published_on)}</span>
                    {article.read_time && <span className="text-xs text-foreground-400 ml-auto">{article.read_time}</span>}
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-800 leading-tight mb-3 line-clamp-2">
                    <Link to="/news" className="hover:text-primary-600 transition-colors">
                      {article.title}
                    </Link>
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-600 hover:text-primary-800 transition-colors"
                  >
                    Read More
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}